import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, PhoneCall, Wallet, FolderOpen, TrendingUp, Plus, Clock } from "lucide-react";
import { useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRODUCTS, fmtEur, fmtDate, fmtDateTime, stageOf } from "@/lib/crm";
import { ReminderRow } from "@/components/ReminderRow";
import { ReminderDialog } from "@/components/ReminderDialog";
import { CompleteReminderDialog } from "@/components/CompleteReminderDialog";
import { MessageDialog } from "@/components/MessageDialog";

const Kpi = ({ icon: I, label, value, testid, accent }) => (
  <div data-testid={testid} className="bg-white border border-slate-200/80 rounded-xl p-5 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow]">
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <I className={`h-4 w-4 ${accent}`} />
    </div>
    <p className="font-display text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
  </div>
);

const Section = ({ title, count, children, action, testid }) => (
  <section data-testid={testid} className="bg-white border border-slate-200/80 rounded-xl overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
      <h3 className="font-display font-semibold text-slate-900">{title} {count != null && <span className="ml-1 text-slate-400 font-normal">({count})</span>}</h3>
      {action}
    </div>
    {children}
  </section>
);

const Empty = ({ text }) => <p className="px-5 py-8 text-sm text-slate-400 text-center">{text}</p>;

export default function Dashboard() {
  const { user } = useAuth();
  const { data } = useApi("/dashboard");
  const [newRem, setNewRem] = useState(false);
  const [complete, setComplete] = useState(null);
  const [msgClient, setMsgClient] = useState(null);
  if (!data) return <div className="text-slate-400" data-testid="dashboard-loading">Caricamento…</div>;
  const k = data.kpi;
  const onMessage = (r) => setMsgClient({ client_id: r.client_id, first_name: r.client_name, last_name: "", phone: r.client_phone, email: r.client_email });
  const totalAmt = Object.values(data.by_product).reduce((s, b) => s + b.amount, 0) || 1;

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">Buongiorno,</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{user?.name?.split(" ")[0]}</h1>
        </div>
        <button data-testid="dashboard-new-reminder-btn" onClick={() => setNewRem(true)} className="flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium hover:border-slate-900 transition-colors">
          <Plus className="h-4 w-4" /> Programma richiamo
        </button>
      </div>

      {(data.overdue.length > 0 || data.today.length > 0) && (
        <div className="flex flex-wrap gap-3" data-testid="urgency-bar">
          {data.overdue.length > 0 && <span className="inline-flex items-center gap-2 rounded-full bg-rose-500 text-white px-4 py-2 text-sm font-semibold"><AlertTriangle className="h-4 w-4" />{data.overdue.length} richiami in ritardo</span>}
          {data.today.length > 0 && <span className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-slate-950 px-4 py-2 text-sm font-semibold"><PhoneCall className="h-4 w-4" />{data.today.length} da richiamare oggi</span>}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <Kpi testid="kpi-today" icon={PhoneCall} label="Richiami oggi" value={data.today.length + data.overdue.length} accent="text-amber-500" />
        <Kpi testid="kpi-pipeline" icon={Wallet} label="Valore pipeline" value={fmtEur(k.pipeline_value)} accent="text-sky-600" />
        <Kpi testid="kpi-active" icon={FolderOpen} label="Pratiche attive" value={k.active_practices} accent="text-purple-600" />
        <Kpi testid="kpi-conversion" icon={TrendingUp} label="Tasso conversione" value={`${k.conversion_rate}%`} accent="text-emerald-600" />
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        <div className="space-y-6">
          {data.overdue.length > 0 && (
            <Section testid="overdue-section" title="In ritardo" count={data.overdue.length}>
              {data.overdue.map((r) => <ReminderRow key={r.reminder_id} r={r} onComplete={setComplete} onMessage={onMessage} />)}
            </Section>
          )}
          <Section testid="today-section" title="Da richiamare oggi" count={data.today.length}>
            {data.today.length ? data.today.map((r) => <ReminderRow key={r.reminder_id} r={r} onComplete={setComplete} onMessage={onMessage} />) : <Empty text="Nessun richiamo per oggi. Ottimo lavoro!" />}
          </Section>
          <Section testid="upcoming-section" title="Prossimi 7 giorni" count={data.upcoming.length} action={<Link to="/calendario" className="text-sm text-sky-700 hover:underline">Calendario</Link>}>
            {data.upcoming.length ? data.upcoming.map((r) => <ReminderRow key={r.reminder_id} r={r} onComplete={setComplete} showDate />) : <Empty text="Nessun richiamo programmato." />}
          </Section>
        </div>

        <div className="space-y-6">
          <Section testid="by-product-section" title="Pipeline per prodotto">
            <div className="p-5 space-y-4">
              {PRODUCTS.map((p) => {
                const b = data.by_product[p.id] || { count: 0, amount: 0 };
                return (
                  <div key={p.id} data-testid={`product-bar-${p.id}`}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: p.color }} />{p.label} <span className="text-slate-400">· {b.count}</span></span>
                      <span className="font-mono text-slate-700">{fmtEur(b.amount)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(b.amount / totalAmt) * 100}%`, background: p.color }} /></div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section testid="deadlines-section" title="Scadenze pratiche">
            {data.deadlines.length ? data.deadlines.map((p) => (
              <Link key={p.practice_id} to={`/clienti/${p.client_id}`} className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                <div><p className="text-sm font-medium">{p.client_name}</p><p className="text-xs text-slate-500">{stageOf(p.stage).label}</p></div>
                <span className="font-mono text-xs flex items-center gap-1 text-slate-600"><Clock className="h-3 w-3" />{fmtDate(p.deadline)}</span>
              </Link>
            )) : <Empty text="Nessuna scadenza impostata." />}
          </Section>

          <Section testid="stale-section" title="Da ricontattare" count={data.stale_clients.length}>
            <p className="px-5 pt-3 text-xs text-slate-500">Nominativi senza contatti da oltre 30 giorni e senza richiami programmati.</p>
            {data.stale_clients.length ? data.stale_clients.map((c) => (
              <Link key={c.client_id} to={`/clienti/${c.client_id}`} className="block px-5 py-2.5 text-sm hover:bg-slate-50">{c.first_name} {c.last_name}</Link>
            )) : <Empty text="Tutti i nominativi sono seguiti." />}
          </Section>

          <Section testid="recent-section" title="Attività recenti">
            {data.recent.length ? data.recent.map((i) => (
              <div key={i.interaction_id} className="px-5 py-3 border-b border-slate-100 last:border-0">
                <p className="text-sm"><span className="font-medium">{i.client_name}</span> <span className="text-slate-400">· {i.type}</span></p>
                <p className="text-xs text-slate-500 truncate">{i.content}</p>
                <p className="text-[11px] font-mono text-slate-400 mt-0.5">{fmtDateTime(i.created_at)}</p>
              </div>
            )) : <Empty text="Nessuna attività ancora." />}
          </Section>
        </div>
      </div>

      <ReminderDialog open={newRem} onOpenChange={setNewRem} />
      <CompleteReminderDialog reminder={complete} onOpenChange={() => setComplete(null)} />
      <MessageDialog open={!!msgClient} onOpenChange={() => setMsgClient(null)} client={msgClient} />
    </div>
  );
}
